import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, ArrowLeft } from "lucide-react";

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/40 text-slate-800 p-6 relative overflow-hidden font-sans">
      {/* Background radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Main content centered directly on screen */}
      <div className="w-full max-w-lg text-center relative z-10 animate-fadeIn flex flex-col items-center">
        
        {/* Animated SVG illustration */}
        <div className="w-full mb-8 select-none">
          <svg
            className="w-full max-w-sm h-56 md:h-64 mx-auto"
            viewBox="0 0 500 300"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Inline CSS styles for SVG animations */}
            <style>{`
              @keyframes float-magnifier {
                0%, 100% { transform: translate(0px, 0px) rotate(0deg); }
                30% { transform: translate(120px, -20px) rotate(15deg); }
                60% { transform: translate(-20px, 30px) rotate(-10deg); }
                80% { transform: translate(60px, 20px) rotate(5deg); }
              }
              @keyframes scan-line {
                0%, 100% { transform: translateY(0px); opacity: 0; }
                10% { opacity: 0.7; }
                90% { opacity: 0.7; }
                50% { transform: translateY(160px); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 0.3; }
                50% { opacity: 1; }
              }
              @keyframes float-doc-1 {
                0%, 100% { transform: translate(50px, 50px) translateY(0px) rotate(0deg); }
                50% { transform: translate(50px, 50px) translateY(-10px) rotate(6deg); }
              }
              @keyframes float-doc-2 {
                0%, 100% { transform: translate(400px, 60px) translateY(0px) rotate(0deg); }
                50% { transform: translate(400px, 60px) translateY(-8px) rotate(-6deg); }
              }
              @keyframes float-shield {
                0%, 100% { transform: translate(90px, 190px) translateY(0px); }
                50% { transform: translate(90px, 190px) translateY(-6px); }
              }
              .anim-magnifier { animation: float-magnifier 7s ease-in-out infinite; transform-origin: 180px 120px; }
              .anim-scan-line { animation: scan-line 4s ease-in-out infinite; }
              .anim-pulse-1 { animation: pulse 3s ease-in-out infinite; }
              .anim-pulse-2 { animation: pulse 2.5s ease-in-out infinite 0.7s; }
              .anim-float-doc-1 { animation: float-doc-1 6s ease-in-out infinite; }
              .anim-float-doc-2 { animation: float-doc-2 5.5s ease-in-out infinite 0.5s; }
              .anim-float-shield { animation: float-shield 7s ease-in-out infinite 1s; }
            `}</style>

            {/* Background Grid Lines (Database/Document theme) */}
            <g stroke="#E2E8F0" strokeWidth="1" opacity="0.7">
              <line x1="50" y1="40" x2="450" y2="40" />
              <line x1="50" y1="80" x2="450" y2="80" />
              <line x1="50" y1="120" x2="450" y2="120" />
              <line x1="50" y1="160" x2="450" y2="160" />
              <line x1="50" y1="200" x2="450" y2="200" />
              <line x1="50" y1="240" x2="450" y2="240" />
              <line x1="100" y1="30" x2="100" y2="250" />
              <line x1="200" y1="30" x2="200" y2="250" />
              <line x1="300" y1="30" x2="300" y2="250" />
              <line x1="400" y1="30" x2="400" y2="250" />
            </g>

            {/* Document Portal Themed Floating Elements */}
            {/* ID Card (Voter ID/DL) */}
            <g className="anim-float-doc-1">
              <rect x="0" y="0" width="40" height="26" rx="4" fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" />
              <circle cx="10" cy="13" r="4" fill="#CBD5E1" />
              <line x1="20" y1="9" x2="32" y2="9" stroke="#E2E8F0" strokeWidth="1.5" />
              <line x1="20" y1="14" x2="28" y2="14" stroke="#E2E8F0" strokeWidth="1.5" />
              <line x1="20" y1="19" x2="32" y2="19" stroke="#E2E8F0" strokeWidth="1.5" />
            </g>

            {/* Document Checklist with verification seal */}
            <g className="anim-float-doc-2">
              <rect x="0" y="0" width="30" height="40" rx="3" fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" />
              <line x1="6" y1="10" x2="24" y2="10" stroke="#E2E8F0" strokeWidth="1.5" />
              <line x1="6" y1="17" x2="24" y2="17" stroke="#E2E8F0" strokeWidth="1.5" />
              <line x1="6" y1="24" x2="18" y2="24" stroke="#E2E8F0" strokeWidth="1.5" />
              <circle cx="22" cy="30" r="3.5" fill="#3B82F6" opacity="0.7" />
            </g>

            {/* Trust Shield / Validated Checkmark */}
            <g className="anim-float-shield">
              <path d="M10 2L2 6v5c0 4.4 3.6 8 8 8s8-3.6 8-8V6l-8-4z" fill="#FFFFFF" stroke="#10B981" strokeWidth="1.5" />
              <path d="M7 9.5l2 2 3-3" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>

            {/* Scanning Laser Beam */}
            <g transform="translate(50, 40)">
              <line x1="0" y1="0" x2="350" y2="0" stroke="#042EF2" strokeWidth="2" strokeDasharray="3" className="anim-scan-line" />
            </g>

            {/* Central "404" bold text with gradient fill */}
            <text
              x="50%"
              y="56%"
              textAnchor="middle"
              fill="url(#grad-404)"
              fontSize="130"
              fontWeight="900"
              fontFamily="'Outfit', 'Inter', sans-serif"
              letterSpacing="-2"
            >
              404
            </text>

            {/* Floating Magnifying Glass searching the screen */}
            <g className="anim-magnifier">
              {/* Magnifier Glass Shadow */}
              <circle cx="183" cy="123" r="32" fill="#0F172A" opacity="0.04" />
              {/* Magnifier Handle */}
              <line x1="202" y1="142" x2="232" y2="172" stroke="#475569" strokeWidth="8" strokeLinecap="round" />
              {/* Magnifier Grip Handle Accent */}
              <line x1="212" y1="152" x2="226" y2="166" stroke="#1E293B" strokeWidth="8" strokeLinecap="round" />
              {/* Magnifier Lens Frame */}
              <circle cx="180" cy="120" r="32" fill="url(#grad-lens)" stroke="#475569" strokeWidth="5.5" />
              {/* Magnifier Lens reflection glare */}
              <path
                d="M 162 108 Q 178 96 194 108"
                stroke="#FFFFFF"
                strokeWidth="2.5"
                strokeLinecap="round"
                opacity="0.6"
              />
            </g>

            {/* Gradients */}
            <defs>
              <linearGradient id="grad-404" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#042EF2" />
                <stop offset="100%" stopColor="#4F46E5" />
              </linearGradient>
              <linearGradient id="grad-lens" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.1" />
                <stop offset="100%" stopColor="#1D4ED8" stopOpacity="0.25" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Messaging */}
        <h2 className="text-3xl font-extrabold tracking-tight text-slate-800 mb-2">
          Page Not Found
        </h2>
        <p className="text-base text-slate-500 max-w-sm mx-auto mb-8 leading-relaxed">
          We couldn't find the page you were looking for.
        </p>

        {/* Interactive Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center w-full max-w-xs">
          <Link
            to="/login"
            className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-btnHover text-white px-5 py-3 rounded-xl font-semibold shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 transition-all duration-200"
          >
            <LogIn size={18} />
            Log In
          </Link>
          <button
            onClick={handleGoBack}
            className="flex-1 flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-slate-50 border border-slate-200 text-slate-600 hover:text-slate-800 px-5 py-3 rounded-xl font-semibold transition-all duration-200"
          >
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
